/**
 * Twemoji rendering helper — converts native Unicode emoji in a string
 * into a list of typed segments so the chat can render every emoji as
 * an identical SVG (served from jsDelivr's twemoji CDN) regardless of
 * the user's OS / browser / installed emoji font.
 *
 * Why this exists
 * ───────────────
 * Native Unicode emoji rendering is platform-dependent:
 *   - macOS / iOS         → Apple Color Emoji (vector, full coverage)
 *   - Windows             → Segoe UI Emoji   (newer Windows; older = tofu)
 *   - Android             → Noto / Fluent    (varies by vendor + version)
 *   - Linux               → often no color emoji font → renders as ▢
 *   - Kiosk / embedded    → unpredictable
 *
 * Twemoji is Twitter's open-source emoji set (now maintained by the
 * jdecked fork) that ships as one SVG per codepoint sequence. By
 * replacing native emoji with <img> tags at render time, every viewer
 * sees the *same* emoji glyph regardless of platform — important for
 * a multi-tenant chat where artists, LMs and admins coordinate across
 * mixed devices.
 *
 * Notes on the codepoint canonicalisation
 * ───────────────────────────────────────
 *   - Variation Selector 16 (FE0F) is stripped from the URL except in
 *     keycap sequences (`X FE0F 20E3`) where Twemoji's filename keeps
 *     it. We special-case keycaps for that reason.
 *   - Skin-tone modifiers (1F3FB–1F3FF) and ZWJ (200D) are preserved.
 *   - Regional indicator pairs (flags) are matched together so e.g.
 *     🇩🇪 (1F1E9 + 1F1EA) becomes a single segment.
 *
 * The regex relies on Unicode property escapes (`\p{...}`), supported in
 * V8 ≥ 64, SpiderMonkey ≥ 78, WebKit ≥ 12. Everywhere we ship to.
 */

const TWEMOJI_CDN = 'https://cdn.jsdelivr.net/gh/jdecked/twemoji@15.1.0/assets/svg'

/**
 * Greedy match of an emoji "grapheme run" — one user-perceived emoji,
 * including any skin-tone modifier, VS16, ZWJ sequence, or regional
 * indicator pair. Built to be over-inclusive on the input side; the
 * canonicaliser below strips the bits Twemoji's URLs don't need.
 *
 * Order of alternatives matters: flag pair first (otherwise the first
 * RI character would be matched as a standalone pictographic).
 */
const EMOJI_RUN_RE = /(?:\p{Regional_Indicator}\p{Regional_Indicator})|(?:\p{Extended_Pictographic}(?:️|⃣|\p{Emoji_Modifier})?(?:‍\p{Extended_Pictographic}(?:️|⃣|\p{Emoji_Modifier})?)*)/gu

export type EmojiSegment =
    | { type: 'text'; value: string }
    | { type: 'unicode_emoji'; codepoints: string; char: string; url: string }

/**
 * Convert a string into a flat list of text / unicode_emoji segments.
 * Non-emoji runs become a single text segment; every emoji becomes a
 * separate `unicode_emoji` node carrying both the original chars (for
 * the alt attribute + clipboard fallback) and the resolved Twemoji URL.
 */
export function splitUnicodeEmoji(text: string): EmojiSegment[] {
    if (!text) return []
    const out: EmojiSegment[] = []
    let lastIdx = 0
    EMOJI_RUN_RE.lastIndex = 0
    let m: RegExpExecArray | null
    while ((m = EMOJI_RUN_RE.exec(text)) !== null) {
        const start = m.index
        const end = start + m[0].length
        if (start > lastIdx) {
            out.push({ type: 'text', value: text.slice(lastIdx, start) })
        }
        const char = m[0]
        const codepoints = toTwemojiCodepoints(char)
        if (codepoints) {
            out.push({
                type: 'unicode_emoji',
                codepoints,
                char,
                url: `${TWEMOJI_CDN}/${codepoints}.svg`
            })
        } else {
            // Couldn't canonicalise (e.g. unmatched VS) — surface as plain
            // text so the OS font still gets a chance.
            out.push({ type: 'text', value: char })
        }
        lastIdx = end
    }
    if (lastIdx < text.length) {
        out.push({ type: 'text', value: text.slice(lastIdx) })
    }
    if (out.length === 0) out.push({ type: 'text', value: text })
    return out
}

/**
 * Canonicalise a grapheme run into Twemoji's dash-joined lowercase hex
 * codepoint string (e.g. "1f469-1f3fc-200d-1f4bb"). Strips Variation
 * Selector 16 in the common case; preserves it inside keycap sequences
 * because Twemoji's filename includes it there.
 */
function toTwemojiCodepoints(emoji: string): string | null {
    if (!emoji) return null
    const codepoints: number[] = []
    for (const ch of emoji) {
        const cp = ch.codePointAt(0)
        if (typeof cp === 'number') codepoints.push(cp)
    }
    if (codepoints.length === 0) return null

    // Detect a keycap sequence (X FE0F 20E3) — keep FE0F in those.
    // Twemoji filenames for keycaps look like "31-fe0f-20e3.svg".
    const isKeycap = codepoints.length >= 3
        && codepoints[1] === 0xFE0F
        && codepoints[2] === 0x20E3

    const filtered = isKeycap
        ? codepoints
        : codepoints.filter(cp => cp !== 0xFE0F)

    if (filtered.length === 0) return null
    return filtered.map(cp => cp.toString(16)).join('-')
}
